import { formatToNonZeroDecimal } from '$lib/utils/numberFormatUtils';
import BigNumber from 'bignumber.js';

/**
 * Parses a value by removing unwanted characters and converting to a number if applicable.
 * @param value - The value to parse.
 * @returns The parsed value.
 */
export function parseValue(value: any): any {
  if (typeof value === 'string') {
    value = value.replace(/[$%,]/g, '');
    return value.includes('%') ? parseFloat(value) : parseFloat(value) || value;
  }
  return value;
}

/**
 * Sorts an array of data based on a specified column and direction.
 * @param data - The array of data to sort.
 * @param column - The column to sort by.
 * @param direction - The sort direction ('asc' or 'desc').
 * @returns A new sorted array.
 */
export function sortTableData<T extends Record<string, any>>(
  data: T[],
  column: keyof T,
  direction: 'asc' | 'desc'
): T[] {
  return [...data].sort((a, b) => {
    const aValue = parseValue(a[column]);
    const bValue = parseValue(b[column]);

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return 0;
  });
}

/**
 * Filters pools based on a search query.
 * @param pools - Array of pools to filter.
 * @param query - Search query string.
 * @returns Filtered array of pools.
 */
export function filterPools(pools: BE.Pool[], query: string): BE.Pool[] {
  if (!query) return pools;
  
  const searchTerm = query.toLowerCase();
  return pools.filter((pool) => {
    return (
      pool.symbol_0.toLowerCase().includes(searchTerm) ||
      pool.symbol_1.toLowerCase().includes(searchTerm) ||
      pool.lp_token_symbol.toLowerCase().includes(searchTerm)
    );
  });
}
